/**
 * IXD 셀 표시 계약 — read-only / editable 양쪽이 동일한 규칙으로 표기 문자열을 만들기 위한 헬퍼.
 *
 * 전제: API는 `column.text`에 raw value(원본값, 예: "123456789")를 내린다.
 * 단위 적용·부호 반전·콤마 포매팅은 모두 렌더링 시점에 수행한다.
 */

import { ATTR } from "@/utils/dom-attrs";
import {
  applyUnit,
  formatDisplayValue,
  formatMonetaryTextForDisplay,
  getUnitDivisor,
  parseNumericValue,
} from "./unit-conversion";
import {
  formatStoredValueForDisplay,
  getIxdSchemaValueKind,
} from "./value-validation";

export interface FormatCellForDisplayOptions {
  /** 원본 raw 문자열 (편집 중이면 임시값, 아니면 column.text) */
  rawText: string;
  schemaType: string | null | undefined;
  /**
   * dlView.unitText (예: "천원"). 내부에서 divisor로 변환.
   * unitDivisor가 함께 주어지면 unitDivisor가 우선한다.
   */
  unitText?: string | null;
  /** 미리 계산된 divisor — 있으면 unitText보다 우선. */
  unitDivisor?: number;
  /**
   * negated 행 여부. 데이터 모델상 라인아이템(concept) 단위 표시 속성이므로
   * row의 innermost row header(`th[scope="row"]`)에서 가져온다.
   */
  negated?: boolean;
  /**
   * .frm CurrencyDecimalDigits — 숫자 kind(monetary·numeric·percent) 소수 자릿수
   * 고정 표기 기준. row header(`th[scope="row"]`)의 decimalDigits. null이면 원본
   * 자릿수 유지 (정수 원본은 정수로 반올림).
   */
  decimalDigits?: number | null;
}

export interface FormattedCell {
  displayText: string;
  /** 숫자 값 셀 여부 (false면 schema 기반 fallback 표기 적용) */
  isNumeric: boolean;
  /** 적용된 unitDivisor (dataset 부착·역변환에 사용) */
  unitDivisor: number;
}

/**
 * monetary 셀: raw → unit 적용 → decimalDigits 반올림 → negated 부호 반전 →
 * "(123,457)"/"(123.46)" 형태. 문자열 십진 연산이라 소수·대형 정수의 정밀도가 유지된다.
 * 비monetary: schema별 표기(`formatStoredValueForDisplay`) — 숫자 kind는 negated 반전·
 * 괄호 음수 표기·decimalDigits 자릿수를 monetary와 같이 적용하고, 단위만 쓰지 않는다.
 *
 * balance는 표기 산출에 영향이 없어 의도적으로 제외했다 (헤더의 차/대변 칩 표시용).
 */
export const formatCellForDisplay = ({
  rawText,
  schemaType,
  unitText,
  unitDivisor: unitDivisorProp,
  negated = false,
  decimalDigits = null,
}: FormatCellForDisplayOptions): FormattedCell => {
  const unitDivisor = unitDivisorProp ?? getUnitDivisor(unitText);
  const isMoney = getIxdSchemaValueKind(schemaType) === "MONETARY";
  const rawNum = parseNumericValue(rawText);
  const isNumeric = rawNum !== null;

  if (isMoney) {
    const displayText = formatMonetaryTextForDisplay({
      rawText,
      divisor: unitDivisor,
      decimalDigits,
      negated,
    });
    if (displayText !== null) {
      return { displayText, isNumeric: true, unitDivisor };
    }
    // 문자열 경로가 못 다루는 숫자 변형(지수 표기, 선행 +, 10^k 아닌 divisor 등)은
    // 기존 Number 경로로 환산한다 — raw 값이 단위 미적용으로 노출되는 것을 막는다
    if (isNumeric) {
      const sign = negated ? -1 : 1;
      return {
        displayText: formatDisplayValue(
          applyUnit(rawNum, unitDivisor) * sign || 0,
        ),
        isNumeric: true,
        unitDivisor,
      };
    }
  }

  return {
    displayText: formatStoredValueForDisplay(
      schemaType,
      rawText,
      negated,
      decimalDigits,
    ),
    isNumeric,
    unitDivisor,
  };
};

/**
 * expectedAutoSumValue는 서버가 이미 표시 문자열까지 완성해서 내려준다.
 * 프론트에서는 단위 변환, negated 반전, 콤마 포매팅을 다시 적용하지 않는다.
 */
export const formatExpectedAutoSumValueForDisplay = (
  expectedAutoSumValue: string | null | undefined,
): string | undefined => {
  if (
    expectedAutoSumValue === null ||
    expectedAutoSumValue === undefined ||
    expectedAutoSumValue.trim() === ""
  ) {
    return undefined;
  }
  return expectedAutoSumValue;
};

/**
 * td 데이터 셀 여부.
 * - th(헤더/라벨/캡션)은 column.text를 그대로 표시해야 하므로 포맷 적용 대상이 아니다.
 * - id가 없는 td도 식별 불가하므로 dataset/포맷 대상에서 제외.
 */

/**
 * 표시값 dataset (paste/delete 경로의 단일 진실 계약).
 * read-only / editable 양쪽이 같은 키를 부착해야 paste 파서가 동일하게 동작한다.
 */
export interface ValueCellDatasetInput {
  /** 저장/편집 경계에서 쓰는 canonical cell id */
  cellId?: string;
  /** API table column이 내려준 원본 id. 검색 fallback용. */
  sourceCellId?: string;
  rawText: string;
  displayText: string;
  /** metadata 9개 필드 기반 동일 값 그룹 키 */
  identityKey?: string;
  schemaType: string | null | undefined;
  unitDivisor: number;
  negated: boolean;
  decimalDigits: number | null | undefined;
  /** abstract 등 편집 불가 표시 (paste/delete가 스킵) */
  readonly?: boolean;
  /** 사용자가 편집한 셀 표시 */
  edited?: boolean;
  /** 축 합계(도메인) 셀 — DSD 워크북 값 채우기 정렬이 이 표시로 합계 열/행을 짝짓는다 */
  axisTotal?: boolean;
}

export const buildValueCellDataset = ({
  cellId,
  sourceCellId,
  rawText,
  displayText,
  identityKey,
  schemaType,
  unitDivisor,
  negated,
  decimalDigits,
  readonly,
  edited,
  axisTotal,
}: ValueCellDatasetInput): Record<string, string | undefined> => ({
  [ATTR.cellId]: cellId || undefined,
  "data-source-cell-id":
    sourceCellId && sourceCellId !== cellId ? sourceCellId : undefined,
  "data-raw-value": rawText,
  "data-display-value": displayText,
  [ATTR.cellIdentityKey]: identityKey || undefined,
  "data-schema-type": schemaType || undefined,
  "data-unit-divisor": String(unitDivisor),
  "data-negated": negated ? "" : undefined,
  "data-decimal-digits":
    decimalDigits !== null && decimalDigits !== undefined
      ? String(decimalDigits)
      : undefined,
  "data-readonly": readonly ? "" : undefined,
  "data-edited": edited ? "" : undefined,
  [ATTR.axisTotal]: axisTotal ? "" : undefined,
});
